import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders
} from 'axios'

import axios from 'axios'
import { environment } from '../../config/environment'
import { logger } from '../../utils/logger'
import { refreshAuthentikToken } from '../auth/authentikAuth'
import { jwtService } from '../auth/jwtService'

// Custom error class to distinguish token refresh failures from other API errors
class TokenRefreshError extends Error {
  constructor(
    message: string,
    public readonly originalError?: unknown
  ) {
    super(message)
    this.name = 'TokenRefreshError'
  }
}

// Token refresh state management
interface TokenRefreshState {
  isRefreshing: boolean
  refreshPromise: Promise<string> | null
  failedRequestsQueue: Array<{
    config: AxiosRequestConfig
    resolve: (value: AxiosResponse) => void
    reject: (error: Error) => void
  }>
}

// API URLs for different environments
const API_URLS = {
  local: 'http://localhost:6050', // Empty for proxy
  staging: 'https://dev.services.leadconnectorhq.com',
}

// Default headers for all requests
const DEFAULT_HEADERS: RawAxiosRequestHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  version: '2021-04-15',
  'source-id': '123'
}

// Helper function to get the base URL based on environment
const getBaseUrl = (): string => {
  // Use local URL when running on localhost
  const isLocal = 
    typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1' ||
     process.env.NODE_ENV === 'development')
  
  const baseUrl = isLocal ? API_URLS.local : API_URLS.staging
  
  logger.log('API Base URL:', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    nodeEnv: process.env.NODE_ENV,
    isLocal,
    baseUrl
  })
  
  return baseUrl
}

// Helper function to retrieve authentication tokens from secure storage
const getAuthToken = (): string | null => {
  try {
    const stored = sessionStorage.getItem('authentik_secure_tokens')
    if (!stored) return null

    const tokenData = JSON.parse(stored)

    // Check if tokens have expired
    if (Date.now() >= tokenData.expiresAt) {
      logger.info('Stored tokens have expired')
      // Clear expired tokens
      sessionStorage.removeItem('authentik_secure_tokens')
      return null
    }

    // Decode and return access token
    const tokens = JSON.parse(atob(tokenData.tokens))
    return tokens.access_token || tokens.id_token
  } catch (error) {
    logger.error('Failed to retrieve auth token:', error)
    return null
  }
}

// Helper function to check if token needs refresh and get refresh token
const getTokenRefreshInfo = (): {
  needsRefresh: boolean
  refreshToken: string | null
  currentToken: string | null
} => {
  try {
    const stored = sessionStorage.getItem('authentik_secure_tokens')
    if (!stored) return { needsRefresh: false, refreshToken: null, currentToken: null }

    const tokenData = JSON.parse(stored)

    // Check if we have a refresh token
    if (!tokenData.refreshToken) {
      return { needsRefresh: false, refreshToken: null, currentToken: null }
    }

    // Decode current tokens
    const tokens = JSON.parse(atob(tokenData.tokens))
    const currentToken = tokens.access_token || tokens.id_token

    if (!currentToken) {
      logger.info('No current token found, will need to refresh')
      return { needsRefresh: true, refreshToken: atob(tokenData.refreshToken), currentToken: null }
    }

    // Check if current token is expired using JWT service
    const isExpired = jwtService.isTokenExpired(currentToken)

    if (isExpired) {
      logger.info('Current token is expired, will need to refresh')
      return { needsRefresh: true, refreshToken: atob(tokenData.refreshToken), currentToken }
    }

    // Token is valid, no refresh needed
    return { needsRefresh: false, refreshToken: null, currentToken }
  } catch (error) {
    logger.error('Failed to check token refresh status:', error)
    return { needsRefresh: false, refreshToken: null, currentToken: null }
  }
}

// Create a class to manage the API client
class ApiClient {
  private client: AxiosInstance
  private baseUrl: string
  private readonly tokenRefreshState: TokenRefreshState = {
    isRefreshing: false,
    refreshPromise: null,
    failedRequestsQueue: []
  }

  constructor() {
    this.baseUrl = getBaseUrl()
    logger.log('Using base URL:', this.baseUrl)

    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: environment.api.timeout,
      headers: DEFAULT_HEADERS
    })

    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        const fullUrl = `${config.baseURL || this.baseUrl}${config.url || ''}`
        logger.log('Making request to:', fullUrl)

        // Always merge DEFAULT_HEADERS with any existing headers
        config.headers = Object.assign({}, DEFAULT_HEADERS, config.headers)

        // Add ISTIO service mesh headers for all environments
        config.headers['channel'] = 'ISTIO_MESH'
        config.headers['istio-workload-name'] = 'platform-devtools-frontend'

        // Proactively check and refresh token if needed
        const authToken = await this.ensureValidToken()
        if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`
          logger.log('Added authentication header to request')
        } else {
          logger.log('No authentication token available for request')
        }

        // Only log headers structure, not full content for security
        logger.log('Request headers set:', {
          hasContentType: !!config.headers['Content-Type'],
          hasAccept: !!config.headers['Accept'],
          hasVersion: !!config.headers['version'],
          hasSourceId: !!config.headers['source-id'],
          hasAuthorization: !!config.headers['Authorization'],
          hasChannel: !!config.headers['channel'],
          hasIstioWorkloadName: !!config.headers['istio-workload-name']
        })

        return config
      },
      (error) => {
        throw error
      }
    )

    // Add response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // If the response has a data property that wraps the actual data, unwrap it
        if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
          response.data = response.data.data
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Handle 401 errors with token refresh retry
        if (error.response?.status === 401 && !originalRequest._retry) {
          logger.info('Received 401 Unauthorized - attempting token refresh')

          // Mark this request as retried to prevent infinite loops
          originalRequest._retry = true

          try {
            // If already refreshing, queue this request
            if (this.tokenRefreshState.isRefreshing) {
              logger.info('Token refresh in progress, queueing request')
              return new Promise((resolve, reject) => {
                this.tokenRefreshState.failedRequestsQueue.push({
                  config: originalRequest,
                  resolve,
                  reject
                })
              })
            }

            // Start token refresh process
            return await this.refreshTokenAndRetry(originalRequest)
          } catch (refreshError) {
            // Only clear auth and redirect if the TOKEN REFRESH itself failed
            // If the retried request failed, let the error propagate normally
            if (refreshError instanceof TokenRefreshError) {
              logger.error('Token refresh failed, redirecting to login:', refreshError)
              this.clearAuthAndRedirect()
              throw this.handleError(error)
            }

            // Retried request failed - propagate error without clearing auth
            logger.info('Retried request failed after successful token refresh')
            throw this.handleError(refreshError)
          }
        }

        // Handle other errors normally
        throw this.handleError(error)
      }
    )
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.request(config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Proactive token refresh before making API calls
  private async ensureValidToken(): Promise<string | null> {
    const { needsRefresh, refreshToken, currentToken } = getTokenRefreshInfo()

    if (!needsRefresh) {
      return currentToken
    }

    if (!refreshToken) {
      logger.info('No refresh token available, cannot refresh')
      return null
    }

    // Double-check pattern: if refresh promise exists, wait for it
    if (this.tokenRefreshState.refreshPromise) {
      logger.info('Token refresh already in progress, waiting...')
      try {
        await this.tokenRefreshState.refreshPromise
        // After refresh completes, get the new token
        return getAuthToken()
      } catch (error) {
        logger.error('Failed to wait for token refresh:', error)
        return null
      }
    }

    // Start proactive token refresh - create promise first to make it available immediately
    logger.info('Starting proactive token refresh')

    // Create the refresh promise
    const refreshPromise = (async () => {
      try {
        const newTokens = await refreshAuthentikToken(refreshToken)

        // Store the new tokens
        const expiresAt = Date.now() + (newTokens.expires_in || 3600) * 1000
        const newTokenData = {
          tokens: btoa(JSON.stringify(newTokens)),
          expiresAt,
          timestamp: Date.now(),
          // If auth server doesn't return a new refresh token, keep the existing one
          refreshToken: newTokens.refresh_token ? btoa(newTokens.refresh_token) : btoa(refreshToken)
        }
        sessionStorage.setItem('authentik_secure_tokens', JSON.stringify(newTokenData))

        logger.info('Proactive token refresh successful')
        return newTokens.access_token
      } catch (error) {
        logger.error('Proactive token refresh failed:', error)
        // Clear auth and redirect on refresh failure
        this.clearAuthAndRedirect()
        throw error
      } finally {
        this.tokenRefreshState.isRefreshing = false
        this.tokenRefreshState.refreshPromise = null
      }
    })()

    // Double-check: another request might have started a refresh while we were creating ours
    const existingRefreshPromise = this.tokenRefreshState.refreshPromise
    if (existingRefreshPromise !== null) {
      logger.info('Another request started refresh, waiting for it...')
      try {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await existingRefreshPromise
        return getAuthToken()
      } catch (error) {
        logger.error('Failed to wait for token refresh:', error)
        return null
      }
    }

    // Store our promise - this is now safe since we double-checked
    this.tokenRefreshState.refreshPromise = refreshPromise
    // Set isRefreshing after promise is assigned to prevent race conditions
    this.tokenRefreshState.isRefreshing = true

    try {
      await this.tokenRefreshState.refreshPromise
      // After refresh completes, get the new token
      return getAuthToken()
    } catch (error) {
      // Refresh failed, return null to indicate no valid token
      logger.error('Proactive token refresh failed while waiting:', error)
      return null
    }
  }

  // Helper method to refresh token and retry failed request
  private async refreshTokenAndRetry(originalRequest: AxiosRequestConfig): Promise<AxiosResponse> {
    // Double-check pattern: if refresh promise exists, wait for it
    if (this.tokenRefreshState.refreshPromise) {
      logger.info('Token refresh already in progress, waiting before retrying request')
      await this.tokenRefreshState.refreshPromise
      // After refresh completes, retry the request with new token
      const newToken = getAuthToken()
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${newToken}`
      }
      return await this.client.request(originalRequest)
    }

    // Create the refresh promise
    const refreshPromise = (async () => {
      let newAccessToken: string

      try {
        logger.info('Starting token refresh process')

        // Get refresh token from storage
        const stored = sessionStorage.getItem('authentik_secure_tokens')
        if (!stored) {
          throw new Error('No stored tokens found')
        }

        const tokenData = JSON.parse(stored)
        if (!tokenData.refreshToken) {
          throw new Error('No refresh token available')
        }

        const refreshToken = atob(tokenData.refreshToken)

        // Call the refresh token function - ONLY catch errors from the refresh itself
        const newTokens = await refreshAuthentikToken(refreshToken)

        // Store the new tokens (using the same storage mechanism)
        const expiresAt = Date.now() + (newTokens.expires_in || 3600) * 1000
        const newTokenData = {
          tokens: btoa(JSON.stringify(newTokens)),
          expiresAt,
          timestamp: Date.now(),
          // If auth server doesn't return a new refresh token, keep the existing one
          refreshToken: newTokens.refresh_token
            ? btoa(newTokens.refresh_token)
            : tokenData.refreshToken
        }
        sessionStorage.setItem('authentik_secure_tokens', JSON.stringify(newTokenData))

        logger.info('Token refresh successful')
        newAccessToken = newTokens.access_token

        // Process queued requests with new token
        this.processQueuedRequests(newAccessToken)

        return newAccessToken
      } catch (error) {
        // Only handle token REFRESH failures here, not retried request failures
        logger.error('Token refresh failed:', error)

        // Wrap in TokenRefreshError to distinguish from API errors
        const refreshError = new TokenRefreshError('Token refresh failed', error)

        // Reject all queued requests since refresh failed
        this.rejectQueuedRequests(refreshError)

        throw refreshError
      } finally {
        // Always mark as not refreshing after refresh attempt completes
        this.tokenRefreshState.isRefreshing = false
        this.tokenRefreshState.refreshPromise = null
      }
    })()

    // Double-check: another request might have started a refresh while we were creating ours
    const existingRefreshPromise = this.tokenRefreshState.refreshPromise
    if (existingRefreshPromise !== null) {
      logger.info('Another request started refresh, waiting for it before retrying...')
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await existingRefreshPromise
      const newToken = getAuthToken()
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${newToken}`
      }
      return await this.client.request(originalRequest)
    }

    // Store our promise - this is now safe since we double-checked
    this.tokenRefreshState.refreshPromise = refreshPromise
    // Set isRefreshing after promise is assigned to prevent race conditions
    this.tokenRefreshState.isRefreshing = true

    // Wait for refresh to complete and get the new token
    // Errors from refresh will propagate as TokenRefreshError
    const newAccessToken = await this.tokenRefreshState.refreshPromise

    // Token refresh succeeded - now retry the original request
    // Errors from the retried request should propagate normally without clearing auth
    logger.info('Retrying original request with new token')

    // Update the authorization header with new token
    originalRequest.headers = originalRequest.headers || {}
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`

    // Retry the original request - if this fails, it's a legitimate API error, not auth failure
    return await this.client.request(originalRequest)
  }

  // Process all queued requests after successful token refresh
  private processQueuedRequests(newAccessToken: string): void {
    logger.info(`Processing ${this.tokenRefreshState.failedRequestsQueue.length} queued requests`)

    for (const queuedRequest of this.tokenRefreshState.failedRequestsQueue) {
      // Update the authorization header
      queuedRequest.config.headers = queuedRequest.config.headers || {}
      queuedRequest.config.headers.Authorization = `Bearer ${newAccessToken}`

      // Retry the request
      this.client
        .request(queuedRequest.config)
        .then((response) => queuedRequest.resolve(response))
        .catch((error) => queuedRequest.reject(error))
    }

    // Clear the queue
    this.tokenRefreshState.failedRequestsQueue = []
  }

  // Reject all queued requests
  private rejectQueuedRequests(error: Error): void {
    logger.info(
      `Rejecting ${this.tokenRefreshState.failedRequestsQueue.length} queued requests due to refresh failure`
    )

    for (const queuedRequest of this.tokenRefreshState.failedRequestsQueue) {
      queuedRequest.reject(error)
    }

    // Clear the queue
    this.tokenRefreshState.failedRequestsQueue = []
  }

  // Clear authentication and redirect to login
  private clearAuthAndRedirect(): void {
    logger.info('Clearing authentication tokens and redirecting to login')

    // Clear tokens from storage
    sessionStorage.removeItem('authentik_secure_tokens')

    // Only redirect if not already on login page
    if (!globalThis.location.pathname.includes('/login')) {
      globalThis.location.href = '/login'
    }
  }

  // Helper method to handle errors
  private handleError(error: unknown): Error {
    // Handle Axios errors
    if (axios.isAxiosError(error) && error.response) {
      // Server responded with error status
      const message =
        error.response.data?.message || error.response.data?.error || 'An error occurred'
      const error_code = error.response.data?.error_code
      const customError = new Error(message)
      ;(customError as Error & { code?: string }).code = error_code
      return customError
    }

    // Handle Axios request errors
    if (axios.isAxiosError(error) && error.request) {
      // Request was made but no response received
      return new Error('No response from server. Please check your internet connection.')
    }

    // Handle other errors
    if (error instanceof Error) {
      return error
    }

    // Fallback for unknown error types
    return new Error('An unexpected error occurred')
  }

  // Wrapper methods for different HTTP methods
  public async get<T>(url: string, params?: unknown): Promise<T> {
    return this.request<T>({ method: 'GET', url, params })
  }

  public async post<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>({ method: 'POST', url, data })
  }

  public async put<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data })
  }

  public async patch<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>({ method: 'PATCH', url, data })
  }

  public async delete<T>(url: string): Promise<T> {
    return this.request<T>({ method: 'DELETE', url })
  }
}

// Create and export a single instance to be used throughout the application
export const apiClient = new ApiClient()

// Export type for TypeScript support
export type { AxiosResponse, AxiosError } from 'axios'
